import { memo, useMemo } from 'react';

import { ImgixImage } from '@air/component-imgix-image';
import { Board } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import {
  RecentlyDeletedTableRow,
  type RecentlyDeletedTableRowProps,
} from '~/components/RecentlyDeleted/components/RecentlyDeletedTableRow';
import { BOARD_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';

export type RecentlyDeletedBoardTableRowProps = Pick<
  RecentlyDeletedTableRowProps,
  'dateDeleted' | 'recoveryWindow' | 'deletedBy' | 'style' | 'title'
> & {
  onRecoverBoard: () => void;
  thumbnail?: string;
  deletedFrom?: { title: string; id: string } | null;
};

export const RecentlyDeletedBoardTableRow = memo(
  ({
    dateDeleted,
    deletedFrom,
    recoveryWindow,
    deletedBy,
    onRecoverBoard,
    style,
    title,
    thumbnail,
  }: RecentlyDeletedBoardTableRowProps) => {
    const fallbackThumbnailUrl = (thumbnail && getCloudFrontUrlFromImgixUrl(thumbnail)) || '';
    const memoizedDeletedFrom = useMemo(() => (deletedFrom ? [deletedFrom] : []), [deletedFrom]);

    const thumbnailEl = useMemo(
      () =>
        thumbnail ? (
          <div className="flex items-center justify-center rounded-4 border border-grey-5 border-solid bg-white p-1">
            <ImgixImage
              className="rounded-2"
              fallbackUrl={fallbackThumbnailUrl}
              alt={title}
              src={thumbnail}
              imgixParams={{
                w: BOARD_THUMBNAIL_DIMENSIONS.width,
                h: BOARD_THUMBNAIL_DIMENSIONS.height,
                fit: 'crop',
                cs: 'origin',
              }}
              {...BOARD_THUMBNAIL_DIMENSIONS}
            />
          </div>
        ) : (
          <div className="flex size-full items-center justify-center rounded-4 border border-grey-5 border-solid bg-white p-1">
            <div className="size-full bg-grey-5" />
          </div>
        ),
      [thumbnail, fallbackThumbnailUrl, title],
    );

    const actionsEl = useMemo(
      () => (
        <Button appearance="ghost" className="-ml-2.5" onClick={onRecoverBoard}>
          Recover
        </Button>
      ),
      [onRecoverBoard],
    );

    return (
      <RecentlyDeletedTableRow
        actions={actionsEl}
        deletedBy={deletedBy}
        dateDeleted={dateDeleted}
        deletedFrom={memoizedDeletedFrom}
        recoveryWindow={recoveryWindow}
        style={style}
        thumbnail={thumbnailEl}
        title={title}
        meta={
          <div className="pointer-events-none flex items-center gap-1">
            <Board className="size-3" />
            <div className="font-semibold text-12 text-grey-11">Board</div>
          </div>
        }
        type="board"
      />
    );
  },
);

RecentlyDeletedBoardTableRow.displayName = 'RecentlyDeletedBoardTableRow';
